/**
 * 钉钉 markdown 消息拼装
 *
 * 机器人启用了「自定义关键词」安全模式，消息内容必须含至少一个关键词才会被投递。
 * 标题由本批事件的级别推导，保证一定命中 DINGTALK_KEYWORDS 之一
 * （详见 docs/frontend-monitoring.md）。
 */
import dayjs from 'dayjs';
import { DINGTALK_KEYWORDS, MONITOR_CONFIG } from '@/monitor/config';
import type { AggregatedEvent, DingtalkMessage, MonitorContext } from '@/types/monitor';

/** 级别图标 */
const LEVEL_ICON: Record<string, string> = { error: '🔴', warn: '🟡' };

/** 环境中文名 */
const ENV_LABEL: Record<string, string> = {
  production: '生产环境',
  development: '开发环境',
};

/** 渲染单个事件为 markdown 片段 */
const renderEvent = (event: AggregatedEvent, ctx: MonitorContext): string => {
  const lines: string[] = [
    `${LEVEL_ICON[event.level] ?? '🔴'} **${event.title}**`,
    '',
    `**描述**：${event.message}`,
  ];
  if (event.source) lines.push(`**位置**：${event.source}`);
  if (event.count > 1) lines.push(`**次数**：${event.count}`);
  Object.entries(event.extra ?? {}).forEach(([key, value]) => {
    lines.push(`**${key}**：${value}`);
  });
  lines.push(`**页面**：${ctx.route || ctx.url}`);
  if (event.stack) {
    const stack = event.stack.split('\n').slice(0, MONITOR_CONFIG.STACK_LINES).join('\n');
    lines.push('', '```', stack, '```');
  }
  lines.push(`**时间**：${dayjs(event.timestamp).format('YYYY-MM-DD HH:mm:ss')}`);
  return lines.join('\n');
};

/**
 * 由本批事件推导标题，保证一定含钉钉关键词。
 * error 级 → 异常报错；warn 级（性能、慢接口）→ 性能问题；两者都有则并列。
 * 仅含限流抑制提示、没有事件的消息回退到 ERROR 关键词，否则同样会被钉钉拒收。
 *
 * 前缀是应用标识：告警群会被多个项目共用，标识放在最前面，
 * 钉钉消息列表的预览行就能直接分辨来源。
 */
const buildTitle = (events: AggregatedEvent[], ctx: MonitorContext): string => {
  const kinds: string[] = [];
  if (events.some((event) => event.level === 'error')) kinds.push(DINGTALK_KEYWORDS.ERROR);
  if (events.some((event) => event.level === 'warn')) kinds.push(DINGTALK_KEYWORDS.PERFORMANCE);
  const label = kinds.length ? kinds.join(' / ') : DINGTALK_KEYWORDS.ERROR;
  return `【${ctx.app}】${label} · ${ENV_LABEL[ctx.env] ?? ctx.env}`;
};

export const formatDingtalk = (
  events: AggregatedEvent[],
  suppressed: number,
  ctx: MonitorContext,
): DingtalkMessage => {
  const title = buildTitle(events, ctx);
  const header = [
    `## ${title}`,
    '',
    `**用户**：${ctx.user} · **浏览器**：${ctx.ua}`,
    `**构建**：${ctx.version} · **会话**：${ctx.sessionId}`,
  ].join('\n');

  const blocks = [header, ...events.map((event) => renderEvent(event, ctx))];
  if (suppressed > 0) blocks.push(`⚠️ 另有 ${suppressed} 条告警被限流抑制`);

  return {
    msgtype: 'markdown',
    markdown: {
      title,
      text: blocks.join('\n\n---\n\n').slice(0, MONITOR_CONFIG.MAX_MESSAGE_LENGTH),
    },
  };
};
